import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { StabilityRuleSource } from '../../../database/schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../../../util/index.js';
import { Status } from '../../../util/schemas.js';
import { itemsDetail, valueItem } from '../../../util/event-log-items.js';
import { EventLogResources, addEventLog } from '../../util.js';
import { findDrugName, findProfileNames } from '../../utils/log-names.js';
import { AddStabilityRuleSchema, createStabilityRule } from '../../utils/stability-rules.js';
export async function createDrugCatalogItem(tx, args, userActions) {
    const newDrugItem = {
        id: uuidv7(),
        catalogType: args.catalogType,
        code: args.code || '',
        name: titleCase(args.name),
        atcCode: args.atcCode,
        nationalDrugId: args.nationalId,
        prescribingName: args.prescribingName,
        description: args.description,
        status: Status.enum.active,
        hasCategories: args.categoriesIds.length > 0,
        ...userActions,
    };
    const promises = [tx.mutate.drugCatalogItems.insert(newDrugItem)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newDrugItem);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.drugCatalogItem,
            resourceId: newDrugItem.id,
            action: 'create',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: { id: newDrugItem.id, name: newDrugItem.name },
        }));
    }
    await Promise.all(promises);
    return newDrugItem;
}
export async function createMedicationProfile(tx, args, userActions) {
    const newMedicationProfile = {
        id: args.id ?? uuidv7(),
        drugCatalogItemId: args.drugItemId,
        medicineFormId: args.medicineFormId,
        reconstitutionRequired: Boolean(args.reconstitutionRequired),
        isDrySubstance: Boolean(args.isDrySubstance),
        name: args.name,
        oralQuantityBasis: args.oralQuantityBasis,
        status: args.status,
        ...userActions,
    };
    const promises = [tx.mutate.medicationProfiles.insert(newMedicationProfile)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newMedicationProfile);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfile,
            resourceId: newMedicationProfile.id,
            action: 'create',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: {
                id: newMedicationProfile.id,
                name: newMedicationProfile.name,
                itemName: await findDrugName(tx, newMedicationProfile.drugCatalogItemId),
            },
        }));
    }
    await Promise.all(promises);
    await Promise.all(args.stabilityRules.map(stabilityRule => createMedicationStabilityRule(tx, { ...stabilityRule, medicationProfileId: newMedicationProfile.id }, userActions)));
    if (args.reconstitutionDiluentsIds) {
        await Promise.all(args.reconstitutionDiluentsIds.map(solutionId => createMedicationProfileReconstitutionDiluent(tx, { medicationProfileId: newMedicationProfile.id, solutionDrugId: solutionId }, userActions)));
    }
    return newMedicationProfile;
}
export async function createMedicationProfileReconstitutionDiluent(tx, args, userActions) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const newMedicationProfileDiluent = {
        id: uuidv7(),
        medicationProfileId: args.medicationProfileId,
        solutionDrugId: args.solutionDrugId,
        createdAt: timestamp,
        createdBy: actor,
    };
    const promises = [tx.mutate.medicationProfileAllowedReconstitutionDiluents.insert(newMedicationProfileDiluent)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newMedicationProfileDiluent);
        const { itemName } = await findProfileNames(tx, args.medicationProfileId);
        const diluent = await findDrugName(tx, args.solutionDrugId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfileAllowedReconstitutionDiluent,
            resourceId: newMedicationProfileDiluent.id,
            action: 'create',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { itemName, ...itemsDetail(valueItem('diluent', diluent)) },
        }));
    }
    await Promise.all(promises);
    return newMedicationProfileDiluent;
}
/**
 * Creates a stability rule and an event log.
 */
export async function createMedicationStabilityRule(tx, args, userActions) {
    const itemName = tx.location === 'server' ? (await findProfileNames(tx, args.medicationProfileId)).itemName : undefined;
    const stabilityRule = await createStabilityRule(tx, { ...args, source: StabilityRuleSource.enum.MEDICATION_PROFILE }, userActions, itemName);
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const newMedicationStabilityRule = {
        id: uuidv7(),
        stabilityRuleId: stabilityRule.id,
        medicationProfileId: args.medicationProfileId,
        createdAt: timestamp,
        createdBy: actor,
    };
    const promises = [tx.mutate.medicationStabilityRules.insert(newMedicationStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newMedicationStabilityRule);
        const { itemName, profileName } = await findProfileNames(tx, args.medicationProfileId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.medicationProfileStabilityRule,
            resourceId: newMedicationStabilityRule.id,
            action: 'create',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { itemName, ...itemsDetail(valueItem('profile', profileName)) },
        }));
    }
    await Promise.all(promises);
    return newMedicationStabilityRule;
}
export async function createMedicationProfiles(tx, args, userActions) {
    await Promise.all(args.medicationProfiles.map(profile => createMedicationProfile(tx, { ...profile, drugItemId: args.drugItemId }, userActions)));
}
export async function createDrugItemCategories(tx, payload, userActions) {
    if (!payload.categories?.length)
        return;
    const relations = payload.categories.flatMap(categoryId => {
        const newDrugItemCategory = {
            id: uuidv7(),
            drugCatalogItemId: payload.drugItemId,
            drugCategoryId: categoryId,
            ...userActions,
        };
        const promises = [tx.mutate.drugCatalogItemsCategories.insert(newDrugItemCategory)];
        if (tx.location === 'server') {
            const patches = createPatch({}, newDrugItemCategory);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItemsCategory,
                resourceId: newDrugItemCategory.id,
                action: 'create',
                patches: patches,
                performedBy: pickActorFromUserActions(userActions),
                performedAt: pickTimestampFromUserActions(userActions),
                status: 'INFO',
            }));
        }
        return promises;
    });
    await Promise.all(relations);
}
//# sourceMappingURL=create.js.map